import { Grid, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { TFunction } from 'i18next';
import React, { FC, useEffect, useMemo } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { NewBasicInfoType } from 'types/new-user';

import DatePicker from '../date-picker/date-picker';
import SelectField from '../select-field';
import TextField from '../text-field';
import { formBasicInfo, PersonalInformationHelperType } from './types';
import { ListingEntry } from 'graphql/listings/queries';
import { CONTACT_TITLE_AGENCY, CONTACT_TITLE_COMPANY_KIND } from 'utils/constants';

const useStyles = makeStyles((theme: any) => ({
  title: {
    color: theme.palette.tab.offselected,
    fontWeight: 500,
  },
}));

type FormPersonalInformationProps = {
  t: TFunction;
  showTitle?: boolean;
  shouldValidate?: boolean;
  showOptionalFields?: boolean;
  helpers?: PersonalInformationHelperType;
  searchText?: string[];
  kindCompanyList?: ListingEntry[],
  kind?: string
  showKind?: boolean
};

const FormPersonalInformation: FC<FormPersonalInformationProps> = ({
  t,
  showTitle,
  shouldValidate,
  showOptionalFields,
  helpers,
  searchText,
  kindCompanyList,
  kind,
  showKind
}) => {
  const classes = useStyles();
  const {
    control,
    getValues,
    reset,
    setValue,
    formState: { errors },
  } = useFormContext<NewBasicInfoType>();

  const genders = useMemo(
    () => [
      {
        value: 'Male',
        text: t('common:male'),
      },
      {
        value: 'Female',
        text: t('common:female'),
      },
    ],
    [t]
  );
  const reservedTitleKinds = kind === CONTACT_TITLE_COMPANY_KIND || kind === CONTACT_TITLE_AGENCY;

  useEffect(() => {
    const currentValues = getValues();

    if (searchText) {
      if (kind === 'Person') {
        const [firstName, middleName, ...rest] = searchText;

        const lastName = rest.length ? rest.join(' ') : middleName || '';

        reset({
          ...currentValues,
          last_name: lastName,
          middle_name: rest.length === 0 ? '' : middleName,
          first_name: firstName,
          kind: 'Person'
        });
      } else {
        const completeNames = searchText?.join(' ') || '';
        reset({
          ...currentValues,
          first_name: completeNames,
        });
      }
    }
    if (kind === 'Person') {
      setValue('kind', 'Person');
    } else {
      setValue('kind', "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kind]);

  return (
    <Grid container direction="row" spacing={2}>
      {showTitle && (
        <Grid item xs={12}>
          <Typography variant="h6" className={classes.title}>
            {t('users:personal-information')}
          </Typography>
        </Grid>
      )}

      <Grid item xs={kind === 'Person' ? 4 : 6}>
        <Controller
          name="first_name"
          control={control}
          render={({ field: { ref, onBlur, ...field } }) => (
            <TextField
              {...field}
              inputRef={ref}
              disabled={helpers!.first_name.disabled}
              label={kind === 'Person' ? t('users:firstName') : t('users:name')}
              handleBlur={() => {
                const error = !!errors.first_name || !!errors.last_name;

                if (!error && helpers!.first_name.onBlur) {
                  helpers!.first_name.onBlur(onBlur);
                }
              }}
              error={!!errors.first_name}
              helperText={
                errors.first_name?.message || (shouldValidate ? t('validations:required') : '')
              }
            />
          )}
        />
      </Grid>
      {showKind &&
        (
          <Grid item xs={6}>
            <Controller
              name="kind"
              control={control}
              render={({ field: { ref, onBlur, ...field } }) => (
                <SelectField
                  {...field}
                  value={field.value || ''}
                  inputRef={ref}
                  data={kindCompanyList || []}
                  dataKey="code"
                  dataValue="code"
                  dataText="description"
                  label={t('users:kind')}
                  emptyValue
                  disabled={reservedTitleKinds}
                  handleBlur={() => {
                    const error = !!errors.kind || !!errors.kind;

                    if (!error && helpers?.kind && helpers!.kind.onBlur) {
                      helpers!.kind.onBlur(onBlur);
                    }
                  }}
                  error={!!errors.first_name}
                  helperText={
                    errors.first_name?.message || (shouldValidate ? t('validations:required') : '')
                  }
                  handleChange={field.onChange}
                />
              )}
            />
          </Grid>
        )}
      {kind === 'Person' && (
        <>
          <Grid item xs={4}>
            <Controller
              name="middle_name"
              control={control}
              render={({ field: { ref, onBlur, ...field } }) => (
                <TextField
                  {...field}
                  inputRef={ref}
                  disabled={helpers!.middle_name.disabled}
                  label={t('users:middleName')}
                  handleBlur={() => {
                    const error = !!errors.first_name || !!errors.last_name;

                    if (!error && helpers!.middle_name.onBlur) {
                      helpers!.middle_name.onBlur(onBlur);
                    }
                  }}
                  error={!!errors.middle_name}
                  helperText={errors.middle_name?.message}
                />
              )}
            />
          </Grid>

          <Grid item xs={4}>
            <Controller
              name="last_name"
              control={control}
              render={({ field: { ref, onBlur, ...field } }) => (
                <TextField
                  {...field}
                  inputRef={ref}
                  disabled={helpers!.last_name.disabled}
                  label={t('users:lastName')}
                  handleBlur={() => {
                    const error = !!errors.first_name || !!errors.last_name;

                    if (!error && helpers!.last_name.onBlur) {
                      helpers!.last_name.onBlur(onBlur);
                    }
                  }}
                  error={!!errors.last_name}
                  helperText={
                    errors.last_name?.message || (shouldValidate ? t('validations:required') : '')
                  }
                />
              )}
            />
          </Grid>
        </>
      )}

      {showOptionalFields && (
        <>
          <Grid item xs={4}>
            <Controller
              name="ssn"
              control={control}
              render={({ field: { ref, onBlur, ...field } }) => (
                <TextField
                  {...field}
                  inputRef={ref}
                  disabled={helpers!.middle_name.disabled}
                  label={kind === 'Person' ? t('users:ssn-atin-itin-ptin') : t('users:ein')}
                  handleBlur={() => {
                    const error = !!errors.first_name || !!errors.last_name;

                    if (!error && helpers!.ssn && helpers!.ssn.onBlur) {
                      helpers!.ssn.onBlur(onBlur);
                    }
                  }}
                  error={!!errors.ssn}
                  helperText={errors.ssn?.message}
                />
              )}
            />
          </Grid>
          {kind === 'Person' && (
            <>
              <Grid item xs={4}>
                <Controller
                  name="birth"
                  control={control}
                  render={({ field: { ref, onBlur, value, ...field } }) => (
                    <DatePicker
                      {...field}
                      inputRef={ref}
                      disableToolbar={false}
                      label={t('users:date-of-birth')}
                      value={value as string | Date}
                      handleBlur={() => {
                        const error = !!errors.first_name || !!errors.last_name;

                        if (!error && helpers!.birth && helpers!.birth.onBlur) {
                          helpers!.birth.onBlur(onBlur);
                        }
                      }}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={4}>
                <Controller
                  name="gender"
                  control={control}
                  render={({ field: { ref, onChange, onBlur, ...field } }) => (
                    <SelectField
                      {...field}
                      inputRef={ref}
                      data={genders}
                      dataKey="value"
                      dataValue="value"
                      dataText="text"
                      label={t('users:gender')}
                      handleChange={onChange}
                      handleBlur={() => {
                        const error = !!errors.first_name || !!errors.last_name;

                        if (!error && helpers!.gender && helpers!.gender.onBlur) {
                          helpers!.gender.onBlur(onBlur);
                        }
                      }}
                      error={!!errors.gender}
                      helperText={errors.gender?.message}
                    />
                  )}
                />
              </Grid>
            </>
          )}
        </>
      )}
    </Grid>
  );
};

FormPersonalInformation.defaultProps = {
  showTitle: true,
  shouldValidate: false,
  showOptionalFields: true,
  helpers: {
    first_name: {},
    last_name: {},
    middle_name: {},
  },
  kind: 'Person'
};

export default FormPersonalInformation;
